import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { GitMerge, ArrowLeftRight, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

const MOTIVO_LABEL = {
  partita_iva: "stessa Partita IVA",
  nome_identico: "stesso nome (a meno di SRL/SPA, punteggiatura…)",
  nome_simile: "nome molto simile",
};

const CAMPO_LABEL = {
  partita_iva: "P.IVA", codice_fiscale: "Cod. Fiscale", indirizzo: "indirizzo",
  citta: "città", cap: "CAP", provincia: "provincia", telefono: "telefono",
  email: "email", sito_web: "sito web", settore: "settore", note: "note",
};

// Iter 31ff: banner "doppioni clienti" — all'ingresso pagina Clienti l'admin
// vede le coppie di probabili doppioni e può collassarle nel cliente con i
// dati più completi. Il collasso ri-aggancia le commesse (e quindi
// rapportini/ore) al principale e sposta il doppione nel cestino.
export default function DuplicatiClientiBanner({ onMerged }) {
  const [candidati, setCandidati] = useState([]);
  const [ignorati, setIgnorati] = useState(new Set()); // chiavi coppia ignorate in sessione
  const [inverted, setInverted] = useState(new Set()); // coppie con direzione invertita
  const [busyKey, setBusyKey] = useState(null);

  const pairKey = (c) =>
    [c.principale.id, c.doppione.id].sort().join("|");

  const load = async () => {
    try {
      const { data } = await api.get("/clienti/merge/candidati");
      setCandidati(Array.isArray(data) ? data : []);
    } catch (_) {
      setCandidati([]);
    }
  };

  useEffect(() => { load(); }, []);

  const visibili = candidati.filter((c) => !ignorati.has(pairKey(c)));
  if (visibili.length === 0) return null;

  const doMerge = async (c) => {
    const key = pairKey(c);
    const swap = inverted.has(key);
    const principale = swap ? c.doppione : c.principale;
    const doppione = swap ? c.principale : c.doppione;
    const ok = window.confirm(
      `Collassare "${doppione.nome}" dentro "${principale.nome}"?\n\n` +
        `Cosa succede:\n` +
        `• Le ${doppione.n_commesse} commesse di "${doppione.nome}" (con i relativi rapportini e ore operai) passano a "${principale.nome}"\n` +
        `• I dati mancanti di "${principale.nome}" vengono completati con quelli del doppione\n` +
        `• "${doppione.nome}" viene spostato nel cestino (recuperabile 30 giorni)\n\n` +
        `L'operazione unifica TUTTO ciò che l'app contiene per quel nome.`
    );
    if (!ok) return;
    setBusyKey(key);
    try {
      const { data } = await api.post("/clienti/merge", {
        principale_id: principale.id,
        doppione_id: doppione.id,
      });
      const campi = (data.campi_completati || [])
        .map((f) => CAMPO_LABEL[f] || f)
        .join(", ");
      toast.success(
        `Doppione collassato in "${principale.nome}": ` +
          `${data.commesse_aggiornate} commesse ri-agganciate` +
          `${data.stabilimenti_aggiunti ? `, ${data.stabilimenti_aggiunti} stabilimenti uniti` : ""}` +
          `${campi ? `, completati: ${campi}` : ""}.`,
        { duration: 7000 }
      );
      await load();
      onMerged?.();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Collasso non riuscito";
      toast.error(typeof msg === "string" ? msg : "Collasso non riuscito");
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <Card
      className="p-4 border-violet-300 bg-violet-50"
      data-testid="duplicati-clienti-banner"
    >
      <div className="flex items-start gap-3">
        <GitMerge className="h-5 w-5 text-violet-700 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0 space-y-3">
          <div>
            <div className="font-heading font-bold text-violet-900">
              {visibili.length === 1
                ? "Trovato 1 probabile doppione tra i clienti"
                : `Trovati ${visibili.length} probabili doppioni tra i clienti`}
            </div>
            <div className="text-xs text-violet-800/80">
              Consiglio: collassa il doppione nel cliente con i dati più
              completi e aggiornati. Commesse, rapportini e ore operai
              verranno unificati.
            </div>
          </div>

          {visibili.map((c) => {
            const key = pairKey(c);
            const swap = inverted.has(key);
            const principale = swap ? c.doppione : c.principale;
            const doppione = swap ? c.principale : c.doppione;
            const busy = busyKey === key;
            return (
              <div
                key={key}
                className="bg-white border border-violet-200 rounded-md p-3 space-y-2"
                data-testid={`duplicato-cliente-${doppione.id}`}
              >
                <div className="text-sm">
                  <b>«{doppione.nome}»</b> sembra un doppione di{" "}
                  <b>«{principale.nome}»</b>{" "}
                  <span className="text-xs text-slate-500">
                    ({MOTIVO_LABEL[c.motivo] || c.motivo}
                    {c.motivo === "nome_simile"
                      ? `, somiglianza ${Math.round(c.similarity * 100)}%`
                      : ""})
                  </span>
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600">
                  <span>
                    ✅ Tieni <b>{principale.nome}</b>: {principale.n_campi_compilati} campi
                    compilati · {principale.n_stabilimenti} stabilimenti ·{" "}
                    {principale.n_commesse} commesse
                  </span>
                  <span>
                    🗑 Collassa <b>{doppione.nome}</b>: {doppione.n_campi_compilati} campi ·{" "}
                    {doppione.n_stabilimenti} stabilimenti · {doppione.n_commesse} commesse
                  </span>
                </div>
                <div className="flex flex-wrap gap-2 pt-1">
                  <Button
                    size="sm"
                    disabled={busy}
                    onClick={() => doMerge(c)}
                    className="h-9 bg-violet-700 hover:bg-violet-800"
                    data-testid={`collassa-cliente-${doppione.id}`}
                  >
                    {busy ? (
                      <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                    ) : (
                      <GitMerge className="h-4 w-4 mr-1.5" />
                    )}
                    Collassa in «{principale.nome}»
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={busy}
                    onClick={() =>
                      setInverted((s) => {
                        const n = new Set(s);
                        n.has(key) ? n.delete(key) : n.add(key);
                        return n;
                      })
                    }
                    className="h-9 border-violet-300 text-violet-800"
                    data-testid={`inverti-merge-${doppione.id}`}
                    title="Scambia quale cliente tenere e quale collassare"
                  >
                    <ArrowLeftRight className="h-4 w-4 mr-1.5" /> Inverti
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={busy}
                    onClick={() =>
                      setIgnorati((s) => new Set(s).add(key))
                    }
                    className="h-9 text-slate-500"
                    data-testid={`ignora-duplicato-${doppione.id}`}
                    title="Nascondi per questa sessione (riapparirà al prossimo accesso)"
                  >
                    <X className="h-4 w-4 mr-1" /> Ignora
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Card>
  );
}
